import React from 'react';
import {View} from 'react-native';
import {createBottomTabNavigator} from '@react-navigation/bottom-tabs';

import {withSafeArea} from '/provider/SafeAreaProvider';
import {withTheme} from '/provider/ThemeProvider';
import {onyxLight} from '/theme/color/on.color';
import {withHOC} from '/util/hoc.util';

import {renderBarIcon} from './BarIcon';
import {renderBarLabel} from './BarLabel';
import {styles, TAB_BAR_HEIGH} from './BottomTabNavigation.style';
import {getAccessibilityLabel} from './BottomTabNavigation.util';
const BottomTab = createBottomTabNavigator();

interface TabItem {
  activeIcon?: string;
  name: string;
  icon: string;
  label: string | React.ReactNode;
  component: React.ComponentType<any>;
  onFocus?: () => void;
  onPress?: ({currentScreen}: {currentScreen: string}) => () => void;
}

interface BottomTabNavigationProps {
  currentScreen: string;
  isPopoverOpen: boolean;
  safeAreaInsets: {
    bottom: number;
    top: number;
    left: number;
    right: number;
  };
  getTheme: (key: string) => string | undefined;
  tabs: TabItem[];
}

export const BottomTabNavigation: React.FC<BottomTabNavigationProps> = ({
  currentScreen = '',
  isPopoverOpen = false,
  safeAreaInsets = {bottom: 0, top: 0, left: 0, right: 0},
  getTheme = noop as (key: string) => string,
  tabs = [],
}) => {
  const screenOptions = ({route = {}}) => {
    const {name: routeName} = route as any;

    return {
      tabBarTestID: routeName,
      tabBarAccessibilityLabel: routeName,
      tabBarIcon: renderBarIcon({routeName, tabs}),
      tabBarLabel: renderBarLabel({routeName, tabs}),
      tabBarActiveTintColor: getTheme('competency.textColor.color'),
      tabBarInactiveTintColor: onyxLight,
      tabBarItemStyle: styles.tabBarTab,
      tabBarStyle: {
        ...styles.tabBar,
        ...(safeAreaInsets
          ? {
              height: TAB_BAR_HEIGH + safeAreaInsets.bottom,
              paddingBottom: safeAreaInsets.bottom,
            }
          : {}),
      },
      // The app is already wrapped in a `<SafeAreaView />` so we don't need
      // to further inset the viewport.
      safeAreaInsets: {
        top: 0,
        bottom: 0,
        left: 0,
        right: 0,
      },
      headerShown: false,
    };
  };

  const renderTab = ({name, component, onFocus, onPress}: any, index: any) => (
    <BottomTab.Screen
      key={name}
      name={name}
      component={component}
      listeners={{
        focus: onFocus,
        tabPress: onPress({currentScreen}),
      }}
      options={{
        tabBarAccessibilityLabel: getAccessibilityLabel({
          name,
          currentScreen,
          index,
          totalTabs: tabs.length,
        }),
      }}
    />
  );

  return (
    <View style={styles.container} accessibilityElementsHidden={isPopoverOpen}>
      <BottomTab.Navigator screenOptions={screenOptions}>
        {tabs.map(renderTab)}
      </BottomTab.Navigator>
    </View>
  );
};

export default withHOC(withTheme, withSafeArea)(BottomTabNavigation);
